import React from 'react';
import { CalculationResults } from '../../types';
import { formatValue, calculateIRR } from '../../utils';

interface AnalyzerSummaryProps {
  calculations: CalculationResults;
}

const AnalyzerSummary: React.FC<AnalyzerSummaryProps> = ({ calculations }) => {
  if (!calculations || !calculations.projectionData) {
    return null;
  }

  const summaryYears = [6, 11, 20];
  const initialEquity = calculations.eigenkapitalSoll;

  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
      {summaryYears.map(year => {
        const yearData = calculations.projectionData[year];
        if (!yearData) return null;

        const {
          immobilienwert,
          restschuld,
          eigentumsanteil,
          cumulativeCashflowNachSteuer,
          cashflowVorSteuer,
          cashflow,
          cumulativeInstandhaltung
        } = yearData;

        const monthlyCFBeforeTax = cashflowVorSteuer / 12;
        const monthlyCFAfterTax = cashflow / 12;

        // Calculate IRR
        const cashflows = [-initialEquity];
        for (let i = 1; i < year; i++) {
          cashflows.push(calculations.projectionData[i].cashflow);
        }
        cashflows.push(calculations.projectionData[year].cashflow + eigentumsanteil);

        const annualReturn = calculateIRR(cashflows);

        let returnColorClass = 'text-yellow-600';
        if (annualReturn < 5) {
          returnColorClass = 'text-red-600';
        } else if (annualReturn > 7) {
          returnColorClass = 'text-green-600';
        }

        return (
          <div key={year} className="bg-gray-50 rounded-xl p-4 shadow-md">
            <h4 className="font-bold mb-2 text-gray-800">Nach {year} Jahren ergeben sich:</h4>
            <p className="mb-1 text-sm">
              <strong>A)</strong> Immobilien Wert {formatValue(immobilienwert, '€')}; 
              minus Restschuld {formatValue(restschuld, '€')} = {formatValue(eigentumsanteil, '€')}
            </p>
            <p className="mb-1 text-sm">
              <strong>B)</strong> Kumuliertes Cashflow {formatValue(cumulativeCashflowNachSteuer, '€')}; 
              davon initial-EK {formatValue(initialEquity, '€')} & eigene Rücklage {formatValue(cumulativeInstandhaltung || 0, '€')}
            </p>
            <p className={`mb-1 text-sm ${returnColorClass}`}>
              ...das macht eine jährliche Rendite von {formatValue(annualReturn, '%')}.
            </p>
            <p className="text-sm">
              <strong>C)</strong> monatliches Cashflow vor St. {formatValue(monthlyCFBeforeTax, '€')} 
              und nach St. {formatValue(monthlyCFAfterTax, '€')}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default AnalyzerSummary;